import { joinURL } from 'ufo'
import { defineProvider } from '@nuxt/image/runtime'

type Modifiers = Record<string, string | number | undefined>

function base64UrlEncode(str: string): string {
    const bytes = new TextEncoder().encode(str)
    let binary = ''
    for (const byte of bytes) binary += String.fromCharCode(byte)
    const base64 = btoa(binary)
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

const OPERATION_KEY_MAP: Record<string, string> = {
    resize: 'rs', rs: 'rs',
    size: 's', s: 's',
    resizingType: 'rt', rt: 'rt',
    width: 'w', w: 'w',
    height: 'h', h: 'h',
    minWidth: 'mw', mw: 'mw',
    minHeight: 'mh', mh: 'mh',
    zoom: 'z', z: 'z',
    dpr: 'dpr',
    enlarge: 'el', el: 'el',
    extend: 'ex', ex: 'ex',
    gravity: 'g', g: 'g',
    crop: 'c', c: 'c',
    padding: 'pd', pd: 'pd',
    trim: 'trim',
    rotate: 'rot', rot: 'rot',
    autoRotate: 'ar', ar: 'ar',
    background: 'bg', bg: 'bg',
    blur: 'bl', bl: 'bl',
    sharpen: 'sh', sh: 'sh',
    pixelate: 'pix', pix: 'pix',
    quality: 'q', q: 'q',
    maxBytes: 'mb', mb: 'mb',
    format: 'f', f: 'f',
    stripMetadata: 'sm', sm: 'sm',
    keepCopyright: 'kcr', kcr: 'kcr',
    stripColorProfile: 'scp', scp: 'scp',
    enforceThumbnail: 'eth', eth: 'eth',
    watermark: 'wm', wm: 'wm',
    preset: 'pr', pr: 'pr',
    cacheBuster: 'cb', cb: 'cb',
    expires: 'exp', exp: 'exp',
    filename: 'fn', fn: 'fn',
    returnAttachment: 'att', att: 'att',
    skipProcessing: 'skp', skp: 'skp',
    raw: 'raw',
}

function buildOperationsPath(modifiers: Modifiers): string {
    return Object.entries(modifiers)
        .filter(([, value]) => value !== undefined && value !== null && value !== '')
        .map(([key, value]) => `${OPERATION_KEY_MAP[key] || key}:${value}`)
        .join('/')
}

export const supportsAlias = true

export default defineProvider({
    getImage(src, options) {
        if (!shouldProxyImage(src)) return { url: src }

        const modifiers = (options?.modifiers ?? {}) as Modifiers
        const config = useRuntimeConfig()
        const imgProxyUrl = config.public.imageProxyURL
        const cb = extractCacheBuster(src)
        let rawPath = resolveImageSrc(src)
        try { rawPath = decodeURIComponent(rawPath) } catch { /* already decoded */ }
        const processedSrc = `local:///${rawPath}`

        const rawOptions = options as any
        const { fit: fitMod, width: wMod, w, height: hMod, h, ...restModifiers } = modifiers
        // Nuxt Image may lift width/height/fit out of modifiers into top-level options
        const fit = fitMod ?? rawOptions.fit
        const width = wMod ?? rawOptions.width
        const height = hMod ?? rawOptions.height
        // Never upscale: a 360px-wide banner requested at w:768 came back as 108KB
        // of invented pixels. Serving the original and letting CSS scale it looks
        // identical and costs a third of the bytes. `contain` re-enables it below,
        // where enlargement is what makes the padding work.
        const extra: Modifiers = { enlarge: 0 }

        if ((width || w) && (height || h)) {
            switch (fit) {
                case 'contain':
                    extra.extend = 1
                    extra.enlarge = 1
                    extra.bg = 'ffffff'
                    break
                case 'fill':
                    extra.resizingType = 'force'
                    break
                case 'inside':
                    extra.resizingType = 'fit'
                    break
                case 'outside':
                    extra.minWidth = width || w
                    extra.minHeight = height || h
                    break
                case 'cover':
                    extra.resizingType = 'fill'
                    break
            }
        }

        const finalModifiers: Modifiers = fit === 'outside'
            ? { ...extra, ...restModifiers, cb }
            : { ...extra, width, w, height, h, ...restModifiers, cb }

        const operationsPath = buildOperationsPath(finalModifiers)
        const url = joinURL(imgProxyUrl, '/insecure', operationsPath, base64UrlEncode(processedSrc))
        return { url }
    }
})
